import { memo, useState, useEffect, useMemo } from 'react';
import { View, SafeAreaView, ActivityIndicator, ScrollView } from 'react-native';
import ViewPortProvider from '../../hooks/ViewPortProvider';
import useViewport from '../../hooks/useViewport';
import THEME from '../../config/theme';
import { useAuthUser } from '../../contexts/useAuthUser';
import {
  doc,
  getDoc,
  orderBy,
  query,
  collection,
  onSnapshot,
  updateDoc,
  deleteField,
} from 'firebase/firestore';
import { firestore } from '../../services/firebase';
import ListHeader from '../../components/ListHeader';
import ViewVideo from './ViewVideo';
import ViewFlatList from './ViewFlatList';
import { StandardText } from '../../config/theme/globalStyles';
import Header from '../../components/Header';
import { useRemoteConfigContext } from '../../contexts/useRemoteConfigContext';
import { useCustomTheme } from '../../contexts/useCustomTheme';
import { Provider } from 'react-native-paper';

/**
 * Layout da aula: vídeo + comentários + barra de ações.
 *
 * ESTE COMPONENTE PRECISA VIVER NO ESCOPO DO MÓDULO. Ele era declarado dentro
 * do corpo da `ClickClass`, o que criava uma função nova — e portanto um TIPO
 * de componente novo — a cada render. Com o tipo mudando, o React não atualiza
 * a árvore: ele DESMONTA tudo e MONTA de novo, recriando o iframe do player, a
 * ListHeader e a coluna de comentários.
 *
 * Como cada snapshot de comentários e cada snapshot de respostas chama
 * `setAllComments`, abrir uma aula com N comentários disparava N+1 remontagens
 * em sequência: era exatamente esse o "pisca" da tela.
 *
 * O `useViewport` continua aqui dentro (e não na ClickClass) porque depende do
 * ViewPortProvider que a própria tela monta logo acima.
 */
const LessonLayout = memo(function LessonLayout({
  user,
  video,
  card,
  cardId,
  categoryId,
  selectedVideo,
  comments,
  hasComments,
  navigation,
  scrollPosition,
  selectedFilter,
}) {
  const { width } = useViewport();
  const breakpoint = 1080;

  return width < breakpoint ? (
    // No celular a PÁGINA INTEIRA rola: vídeo, título/descrição e comentários
    // sobem juntos. Antes o vídeo e o cabeçalho da aula ficavam parados numa
    // coluna de altura fixa e só a lista de comentários rolava por dentro.
    //
    // Consequência disso: aqui não pode existir uma segunda rolagem no mesmo
    // eixo. Dois scrolls aninhados brigam pelo toque — o dedo sobre os
    // comentários rolaria a lista interna e nunca a página. Por isso o
    // VideoComments empilha os itens numa coluna simples abaixo do breakpoint,
    // sem FlatList rolável.
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ paddingBottom: '2rem' }}
    >
      <View
        style={{
          alignSelf: 'center',
          width: '100%',
          height: (width - 32) / 1.78,
          marginBottom: '1rem',
          // 1rem: mesma margem lateral do conteúdo abaixo, igual à ClickCard.
          paddingHorizontal: '1rem',
        }}
      >
        <ViewVideo
          user={user}
          video={video}
          card={card}
          cardId={cardId}
          categoryId={categoryId}
          selectedVideo={selectedVideo}
        />
      </View>
      <View style={{ paddingHorizontal: '1rem' }}>
        <ListHeader
          cardId={cardId}
          categoryId={categoryId}
          video={video}
          videoId={selectedVideo}
          navigation={navigation}
          user={user}
          login={true}
        />
        <ViewFlatList
          comments={comments}
          videoId={selectedVideo}
          categoryId={categoryId}
          cardId={cardId}
          login={user}
        />
      </View>
    </ScrollView>
  ) : (
    /* Sem padding no topo: a capa/vídeo começa logo abaixo do Header,
       igual à ClickCard. O respiro embaixo continua. */
    <ScrollView contentContainerStyle={{ paddingHorizontal: '2%', paddingBottom: '2rem' }}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'center',
        }}
      >
        {/* LADO ESQUERDO: VÍDEO */}
        <View
          style={{
            alignSelf: 'center',
            width: '70%',
            height: (width * 0.7 - 32) / 1.78,
          }}
        >
          <ViewVideo
            user={user}
            video={video}
            card={card}
            cardId={cardId}
            categoryId={categoryId}
            selectedVideo={selectedVideo}
          />
        </View>

        {/* LADO DIREITO: FLATLIST ou ESPAÇO RESERVADO */}
        <View
          style={{
            alignSelf: 'center',
            width: '30%',
            height: (width * 0.7 - 32) / 1.78,
            // Igual à ClickCard: 1rem só como calha entre o vídeo e a
            // coluna da direita; a borda direita segue a margem da página.
            paddingLeft: '1rem',
          }}
        >
          {hasComments ? (
            <ViewFlatList
              comments={comments}
              videoId={selectedVideo}
              categoryId={categoryId}
              cardId={cardId}
              login={user}
            />
          ) : null}
        </View>
      </View>

      {/* LISTHEADER SEMPRE EMBAIXO */}
      {/* 2rem: espaçamento vertical fixo, igual ao paddingVertical da
          página. Antes era 2% — percentual da LARGURA, que virava um vão
          enorme em telas largas e sumia em telas estreitas. */}
      <View style={{ marginTop: '2rem' }}>
        <ListHeader
          cardId={cardId}
          categoryId={categoryId}
          video={video}
          videoId={selectedVideo}
          navigation={navigation}
          user={user}
          login={true}
        />
      </View>
    </ScrollView>
  );
});

export function ClickClass({ route, navigation }) {
  const { cardId, categoryId, selectedVideo, scrollPosition, selectedFilter, productCatalogOrigin } = route.params;
  const config = useRemoteConfigContext();
  const hasComments = config.hasComments;
  const { customColor, customFont } = useCustomTheme();

  const [card, setCard] = useState();
  const [video, setVideo] = useState([]);
  // `undefined` até o primeiro snapshot chegar. A coluna usa isso para
  // distinguir "carregando" de "não há comentários" — antes os dois eram `[]`,
  // e era por isso que o VideoComments tinha um setTimeout de 500ms fingindo
  // um carregamento que ninguém sabia quando terminava.
  const [allComments, setAllComments] = useState(undefined);
  // Sessão e documento `users/{uid}` já resolvidos no boot (AuthUserProvider).
  const { user: userDoc, userId, isLogged, isLoadingUser } = useAuthUser();
  const user = useMemo(
    () => (isLogged && userDoc ? { id: userId, ...userDoc } : undefined),
    [isLogged, userDoc, userId]
  );
  const [isLoading, setIsLoading] = useState(true);

  const [isCardLoaded, setIsCardLoaded] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const isUserLoaded = !isLoadingUser;

  // ESTA TELA É REUTILIZADA ENTRE AULAS.
  //
  // Vindo da home, o "voltar" da aula faz `navigate('Modulo')`, que EMPILHA o
  // módulo em cima da aula. Ao abrir a próxima aula, o React Navigation
  // encontra a rota "Aula" já na pilha, volta para esta mesma instância e
  // apenas troca os params — o componente não desmonta.
  //
  // Sem zerar o estado aqui, `card`, `video` e `allComments` continuavam sendo
  // os da aula ANTERIOR e `isLoading` já estava `false`: a tela pintava o
  // conteúdo velho até o fetch novo responder. Era o "pisca com a imagem do
  // conteúdo anterior".
  //
  // O ajuste é feito DURANTE O RENDER, e não dentro de um `useEffect`, porque
  // efeito roda depois da pintura — daria pelo menos um frame com a aula
  // errada na tela. Assim o React descarta este render e refaz com o estado
  // limpo antes de pintar qualquer coisa.
  const lessonKey = `${categoryId}/${cardId}/${selectedVideo}`;
  const [loadedLesson, setLoadedLesson] = useState(lessonKey);

  if (loadedLesson !== lessonKey) {
    setLoadedLesson(lessonKey);
    setCard(undefined);
    setVideo([]);
    setAllComments(undefined);
    setIsCardLoaded(false);
    setIsVideoLoaded(false);
    setIsLoading(true);
  }

  const toDate = (seconds) => {
    const date = new Date(1970, 0, 1);
    date.setSeconds(seconds);
    return date;
  };

  // Os dois `fetch` marcam a flag de carregado no `finally`, inclusive quando o
  // documento não existe ou a leitura falha: antes davam `return` sem marcar
  // nada, e um card apagado (ou uma queda de rede) prendia a tela no spinner
  // para sempre — sem mensagem nenhuma.
  const fetchCard = async () => {
    try {
      const docRef = doc(firestore, 'categories', categoryId, 'cards', cardId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) return;
      setCard(docSnap.data());
    } catch (error) {
      console.error('Erro ao carregar o módulo:', error);
    } finally {
      setIsCardLoaded(true);
    }
  };

  const fetchVideo = async () => {
    try {
      const docRef = doc(
        firestore,
        'categories',
        categoryId,
        'cards',
        cardId,
        'videos',
        selectedVideo
      );
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) return;
      setVideo(docSnap.data());
    } catch (error) {
      console.error('Erro ao carregar a aula:', error);
    } finally {
      setIsVideoLoaded(true);
    }
  };

  const hideCommentNotification = async () => {
    const cardRef = doc(firestore, 'categories', categoryId, 'cards', cardId);
    const videoRef = doc(
      firestore,
      'categories',
      categoryId,
      'cards',
      cardId,
      'videos',
      selectedVideo
    );

    try {
      const cardSnap = await getDoc(cardRef);
      const videoSnap = await getDoc(videoRef);

      if (cardSnap?.data()?.hasNewComment) {
        await updateDoc(cardRef, { hasNewComment: deleteField() });
      }

      if (videoSnap?.data()?.hasNewComment) {
        await updateDoc(videoRef, { hasNewComment: deleteField() });
      }
    } catch (error) {
      // Aluno sem permissão de escrita no card cai aqui. Não é motivo para
      // barrar a aula: o aviso é do admin, não dele.
      console.error('Erro ao limpar o aviso de novo comentário:', error);
    }
  };

  // Limpar o aviso de "novo comentário" independe de a aba de comentários
  // estar habilitada — antes isso vivia dentro do efeito dos comentários.
  //
  // Só o ADMIN limpa. O aviso existe para ele saber que chegou comentário
  // novo, e o efeito rodava para todo mundo: o primeiro aluno que abrisse a
  // aula apagava o badge antes de o admin ver.
  useEffect(() => {
    if (user?.isAdmin !== true) return;
    hideCommentNotification();
  }, [categoryId, cardId, selectedVideo, user?.isAdmin]);

  // Comentários + respostas.
  //
  // A versão anterior só chamava `setAllComments` quando um contador de
  // respostas processadas batia EXATAMENTE com o número de comentários. Uma
  // única assinatura de respostas que não disparasse (erro de permissão, doc
  // removido no meio) deixava a lista inteira sem publicar — e como o mesmo
  // sinal também liberava o loading da tela, o efeito colateral era a coluna
  // vazia/tela travada. Aqui os comentários são publicados assim que chegam e
  // as respostas entram depois, cada uma no seu tempo.
  //
  // A limpeza também estava quebrada: `findAllComments` era `async`, então o
  // efeito devolvia uma Promise em vez da função de unsubscribe e NADA era
  // cancelado — as assinaturas se acumulavam a cada navegação.
  useEffect(() => {
    if (!hasComments) {
      setAllComments([]);
      return;
    }

    const commentsRef = query(
      collection(
        firestore,
        'categories',
        categoryId,
        'cards',
        cardId,
        'videos',
        selectedVideo,
        'comments'
      ),
      orderBy('createdAt', 'desc')
    );

    let baseComments = [];
    const repliesByComment = new Map();
    const replyUnsubscribes = new Map();

    const publish = () => {
      setAllComments(
        baseComments.map((comment) => ({
          ...comment,
          replies: repliesByComment.get(comment.id) || [],
        }))
      );
    };

    const unsubscribeComments = onSnapshot(
      commentsRef,
      (querySnapshot) => {
        baseComments = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        const currentIds = new Set(baseComments.map((comment) => comment.id));

        // Cancela o que sobrou de comentários que não existem mais.
        replyUnsubscribes.forEach((unsubscribe, id) => {
          if (currentIds.has(id)) return;
          unsubscribe();
          replyUnsubscribes.delete(id);
          repliesByComment.delete(id);
        });

        baseComments.forEach((comment) => {
          if (replyUnsubscribes.has(comment.id)) return;

          const repliesRef = query(
            collection(
              firestore,
              `categories/${categoryId}/cards/${cardId}/videos/${selectedVideo}/comments/${comment.id}/replies`
            ),
            orderBy('createdAt', 'desc')
          );

          replyUnsubscribes.set(
            comment.id,
            onSnapshot(
              repliesRef,
              (replySnapshot) => {
                repliesByComment.set(
                  comment.id,
                  replySnapshot.docs.map((replySnap) => ({
                    id: replySnap.id,
                    ...replySnap.data(),
                  }))
                );
                publish();
              },
              (error) =>
                console.error('Erro ao carregar respostas do comentário:', error)
            )
          );
        });

        // Publica sem esperar as respostas.
        publish();
      },
      (error) => console.error('Erro ao carregar comentários:', error)
    );

    return () => {
      unsubscribeComments();
      replyUnsubscribes.forEach((unsubscribe) => unsubscribe());
      replyUnsubscribes.clear();
    };
  }, [categoryId, cardId, selectedVideo, hasComments]);

  useEffect(() => {
    fetchCard();
    fetchVideo();
  }, [categoryId, cardId, selectedVideo]);

  // Os comentários NÃO entram mais nesta espera: eles carregam na própria
  // coluna (o VideoComments tem o seu indicador). Antes a tela inteira ficava
  // refém da assinatura de comentários — e como o estado inicial era
  // `useState(!hasComments)`, o comportamento mudava conforme o remote config
  // já estivesse resolvido ou não no momento em que a tela montava.
  useEffect(() => {
    if (isCardLoaded && isVideoLoaded && isUserLoaded) {
      setIsLoading(false);
    }
  }, [isCardLoaded, isVideoLoaded, isUserLoaded]);

  // Mesma regra de acesso de antes, apenas retirada do meio do JSX: era uma
  // condicional de trinta linhas dentro do `return`, reavaliada a cada render.
  const hasAccess = useMemo(
    () =>
      (card?.productIds &&
        card?.productIds?.length > 0 &&
        card?.productIds?.filter((cardProductId) => {
          const matchingUserProducts = user?.productIds?.filter(
            (userProduct) => userProduct.productId === cardProductId
          );
          if (!matchingUserProducts || matchingUserProducts.length === 0) {
            return false; // O usuário não possui este produto, então não tem acesso
          }
          // Se não houver data de expiração, ou se alguma data de expiração for maior que a data atual, o usuário tem acesso
          return matchingUserProducts.some(
            (userProduct) =>
              !userProduct.expirationDate ||
              toDate(userProduct.expirationDate.seconds) > new Date()
          );
        }).length > 0) ||
      (card?.plans &&
        card?.plans.length > 0 &&
        card?.plans.filter(
          (plan) => (plan.priceId || plan.productId) == user?.plan
        ).length > 0) ||
      (user?.courses &&
        user?.courses.length > 0 &&
        user?.courses.filter(
          (course) =>
            card?.coursesCard?.some(
              (itemToBeRemoved) =>
                (itemToBeRemoved.priceId || itemToBeRemoved.productId) ===
                (course.priceId || course.productId)
            ) && toDate(course.dueDate.seconds) > new Date()
        ).length > 0) ||
      (card?.plans == null &&
        card?.coursesCard == null &&
        card?.productIds == null) ||
      user?.isAdmin == true,
    [card, user]
  );

  if (isLoading) {
    return (
      <ActivityIndicator
        style={{
          flex: 1,
          backgroundColor:
            customColor && customColor.background?.app
              ? customColor.background.app
              : THEME.COLORS.BACKGROUND_MAIN,
          justifyContent: 'center',
        }}
        color={
          customColor && customColor.primary
            ? customColor.primary
            : THEME.COLORS.PRIMARY_800
        }
      />
    );
  }


  return (
    // O `Provider` do react-native-paper vive AQUI, envolvendo a tela uma vez
    // só. Ele estava dentro do layout: cada remontagem recriava o contexto de
    // tema e o Portal.Host junto com o resto.
    <Provider>
      <ViewPortProvider>
        <SafeAreaView
          style={{
            backgroundColor:
              customColor && customColor.background?.app
                ? customColor.background.app
                : THEME.COLORS.BACKGROUND_MAIN,
            flexGrow: 1,
          }}
        >
          {/* `user` é o que faz o Header saber que é admin: sem ele a tela caía
              no cabeçalho de aluno (sem a logo à direita), diferente da
              ClickCard. */}
          <Header
            onPress={() => {
              // Aberta pela central de comentários: volta para a lista, e não
              // para o módulo. Sem histórico (link aberto direto), segue o normal.
              if (route.params?.fromComments && navigation.canGoBack()) {
                navigation.goBack();
                return;
              }
              // merge: true — sem isto, `navigate` SUBSTITUI os params da rota
              // "Modulo" já na pilha; se este componente não tiver recebido
              // productCatalogOrigin (ex.: veio de uma Live que não repassou),
              // o Módulo original perderia o vínculo com a CatalogoProdutos.
              navigation.navigate({
                name: 'Modulo',
                params: {
                  cardId: cardId,
                  categoryId: categoryId,
                  scrollPosition: scrollPosition,
                  selectedFilter: selectedFilter,
                  ...(productCatalogOrigin && { productCatalogOrigin }),
                },
                merge: true,
              });
            }}
            user={user}
          />
          {hasAccess ? (
            <LessonLayout
              user={user}
              video={video}
              card={card}
              cardId={cardId}
              categoryId={categoryId}
              selectedVideo={selectedVideo}
              comments={allComments}
              hasComments={hasComments}
              navigation={navigation}
              scrollPosition={scrollPosition}
              selectedFilter={selectedFilter}
            />
          ) : (
            <View
              style={{
                justifyContent: 'center',
                flex: 1,
                alignItems: 'center',
                marginHorizontal: '0.5rem',
              }}
            >
              <StandardText
                style={{
                  fontFamily:
                    customFont && customFont.fontFamilySubtitle
                      ? customFont.fontFamilySubtitle
                      : THEME.FONTFAMILY.MEDIUM,
                }}
                color={
                  customColor && customColor.text && customColor.text.auth
                    ? customColor.text.auth
                    : THEME.COLORS.TEXT_ABOUT
                }
              >
                Ops, você não possui acesso a esse conteúdo!
              </StandardText>
            </View>
          )}
        </SafeAreaView>
      </ViewPortProvider>
    </Provider>
  );
}